import { useNavigate } from 'react-router-dom';
import {
	ActionButton,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	SectionCard,
} from '@practium/ui';
import type { SectionTab } from '@practium/ui';
import { PiePercentageChart, ComparisonLineChart } from '@practium/features/dashboard';
import { usePermissions } from '@practium/features/permissions';
import { LandingPageSkeleton } from '../components/Skeletons/LandingPageSkeleton';
import { LandingPlansTable } from '../components/Tables/LandingPlansTable/LandingPlansTable';
import { TestimonialsTable } from '../components/Tables/TestimonialsTable/TestimonialsTable';
import { useLandingPage } from '../hooks/useLandingPage';
import type { LandingTab } from '../types/landing.types';
import {
	LANDING_PIE_TITLE,
	LANDING_PIE_SUBTITLE,
	LANDING_SECTION_TITLE,
	LANDING_SECTION_SUBTITLE,
} from '../constants/page.constants';
import { LANDING_ROUTES } from '../constants/landing.constants';
import {
	LANDING_PLANS_PIE_DATA,
	LANDING_PLANS_LINE_DATA,
	TESTIMONIALS_LINE_DATA,
} from '../constants/charts.constants';

export const LandingControlPage = () => {
	const navigate = useNavigate();
	const { abilities } = usePermissions();
	const canCreateTestimonial = abilities.can('create', 'testimonials');

	const {
		activeTab,
		setActiveTab,
		filteredPlans,
		filteredTestimonials,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		isLoading,
	} = useLandingPage();

	if (isLoading) return <LandingPageSkeleton />;

	const lineData = activeTab === 'plans' ? LANDING_PLANS_LINE_DATA : TESTIMONIALS_LINE_DATA;
	const lineTitle = activeTab === 'plans' ? 'Cliques nos Planos' : 'Likes em Depoimentos';
	const lineMainValue = activeTab === 'plans' ? '542 Cliques Hoje' : '2 Likes Hoje';
	const lineFooter =
		activeTab === 'plans' ? 'Cliques de 00:00 – 23:59' : 'Likes de 1–6 Dez, 2025';
	const lineLabel = activeTab === 'plans' ? 'Dia atual' : 'Semana atual';

	const tabs: SectionTab<LandingTab>[] = [
		{
			key: 'plans',
			label: 'Planos',
			content: (
				<LandingPlansTable
					plans={filteredPlans}
					onRowClick={id => navigate(LANDING_ROUTES.VIEW_PLAN(id))}
				/>
			),
		},
		{
			key: 'testimonials',
			label: 'Depoimentos',
			content: (
				<TestimonialsTable
					testimonials={filteredTestimonials}
					onRowClick={id => navigate(LANDING_ROUTES.VIEW_TESTIMONIAL(id))}
				/>
			),
		},
	];

	return (
		<ListPageLayout
			sidebarWidth="28rem"
			sidebarW1500="22rem"
			sidebarW1300="19rem"
			sidebarW1200="17rem"
			sidebar={
				<ListPageCharts single={activeTab !== 'plans'}>
						{activeTab === 'plans' && (
							<ListPageChartCard>
								<PiePercentageChart
									title={LANDING_PIE_TITLE}
									subtitle={LANDING_PIE_SUBTITLE}
									buttonText="Ver Relatório"
									onButtonClick={() => {}}
									data={LANDING_PLANS_PIE_DATA}
								/>
							</ListPageChartCard>
						)}
						<ListPageChartCard maxHeight="30rem">
							<ComparisonLineChart
								title={lineTitle}
								mainValue={lineMainValue}
								trendValue="12.3%"
								trendLabel={activeTab === 'plans' ? 'vs dia anterior' : 'vs semana anterior'}
								trendDirection="down"
								footerText={lineFooter}
								buttonText="Ver Relatório"
								onButtonClick={() => {}}
								data={lineData}
								xAxisKey="day"
								line1Key="atual"
								line1Label={lineLabel}
								line2Key="anterior"
								line2Label={activeTab === 'plans' ? 'Dia anterior' : 'Semana anterior'}
								tooltipTitle={activeTab === 'plans' ? 'Cliques' : 'Likes'}
							/>
						</ListPageChartCard>
					</ListPageCharts>
			}
		>
			<SectionCard
				title={LANDING_SECTION_TITLE}
				subtitle={LANDING_SECTION_SUBTITLE}
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				search={true}
				searchQuery={searchQuery}
				onSearch={setSearchQuery}
				sortAsc={sortAsc}
				onToggleSort={toggleSort}
				filterSlot={
					filterGroups.length > 0 ? (
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={filterGroups}
							placeholder="Filtros"
						/>
					) : undefined
				}
				footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				headerAction={
					<>
						{activeTab === 'testimonials' && canCreateTestimonial && (
							<ActionButton
								text="Adicionar"
								onClick={() => navigate(LANDING_ROUTES.NEW_TESTIMONIAL)}
							/>
						)}
						<ActionButton text="Ver Relatório" onClick={() => {}} />
					</>
				}
			/>
		</ListPageLayout>
	);
};
